import { ButtonVisibilities, ViewManagerView } from './viewManagerTypes';
import { ViewManagerUI } from './ViewManagerUI';

export class ViewManager {
    views: ViewManagerView[];

    private viewManagerEl: HTMLElement;
    private shareButton: HTMLButtonElement;
    private saveButton: HTMLButtonElement;
    private editButton: HTMLButtonElement;
    private deleteButton: HTMLButtonElement;
    private addButton: HTMLButtonElement;
    private selectElement: HTMLSelectElement;
    private viewManagerHandlers: ViewManagerHandlers;

    constructor(viewManagerHandlers: ViewManagerHandlers) {
        this.viewManagerHandlers = viewManagerHandlers;
    }

    async init(container: HTMLElement) {
        container.innerHTML = ViewManagerUI.viewManagerHtml;

        this.viewManagerEl = container.querySelector('.view-manager');

        const classPrefix = '.view-manager__';
        this.addButton = this.viewManagerEl.querySelector(`${classPrefix}btn-add`);
        this.editButton = this.viewManagerEl.querySelector(`${classPrefix}btn-edit`);
        this.saveButton = this.viewManagerEl.querySelector(`${classPrefix}btn-save`);
        this.deleteButton = this.viewManagerEl.querySelector(`${classPrefix}btn-delete`);
        this.selectElement = this.viewManagerEl.querySelector(`${classPrefix}select`);
        this.shareButton = this.viewManagerEl.querySelector(`${classPrefix}btn-is-shared`);

        this.addButton.addEventListener('click', (event) => this.viewManagerHandlers.addButtonHandler(event, this));
        this.editButton.addEventListener('click', (event) => this.viewManagerHandlers.editButtonHandler(event, this));
        this.saveButton.addEventListener('click', (event) => this.viewManagerHandlers.saveButtonHandler(event, this));
        this.deleteButton.addEventListener('click', (event) => this.viewManagerHandlers.deleteButtonHandler(event, this));
        this.shareButton.addEventListener('click', (event) => this.viewManagerHandlers.shareButtonHandler(event, this));

        $(this.selectElement).on('change', (event, data) => {
            if (!data || data.triggerChange) {
                this.viewManagerHandlers.select2ChangeHandler(this);
            }
        });

        await this.initializeSelect2();
    }

    getSelectedViewId(): string {
        return $(this.selectElement).val()?.toString() ?? null;
    }

    async setSelectedViewId(viewId: string, triggerChange: boolean = true) {
        $(this.selectElement).val(viewId);
        ($(this.selectElement) as any).trigger('change', { triggerChange: false });
        if (triggerChange) {
            await this.viewManagerHandlers.select2ChangeHandler(this);
        }
    }

    getSelectedViewName(): string {
        return $(this.selectElement).find(':selected').text();
    }

    updateButtonVisibilities(buttonVisibilities: ButtonVisibilities) {
        this.editButton.hidden = !buttonVisibilities.ShowEditButton;
        this.saveButton.hidden = !buttonVisibilities.ShowSaveButton;
        this.deleteButton.hidden = !buttonVisibilities.ShowDeleteButton;
        this.addButton.hidden = !buttonVisibilities.ShowAddButton;
        this.shareButton.hidden = !buttonVisibilities.ShowShareButton;
    }

    setIsPrivate(isPrivate: boolean) {
        const spanEl = this.shareButton.querySelector('span');

        if (isPrivate) {
            spanEl.classList.add('fa-lock');
            spanEl.classList.remove('fa-unlock');
        } else {
            spanEl.classList.add('fa-unlock');
            spanEl.classList.remove('fa-lock');
        }
    }

    getViewNameById(originalViewId: string) {
        const viewId = originalViewId ?? '';
        const view = $(this.selectElement).find(`option[value='${viewId}']`);
        return view.text();
    }

    async refreshOptions() {
        ($(this.selectElement) as any).select2('destroy');
        await this.initializeSelect2();
    }

    private async renderAvailableViewOptions(): Promise<void> {
        this.selectElement.innerHTML = '';
        try {
            if (this.views !== undefined && this.views !== null) {
                this.views.forEach((view) => {
                    const option = document.createElement('option');
                    option.value = view.Id;
                    option.innerText = view.DisplayName;
                    if (!view.IsVisible) {
                        option.classList.add('hidden');
                    }
                    if (view.IsActive) {
                        this.updateButtonVisibilities(view.ButtonVisibilities);
                        this.setIsPrivate(view.IsPrivate);
                        option.setAttribute('selected', '');
                    }
                    this.selectElement.appendChild(option);
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    private async initializeSelect2() {
        await this.updateViews();
        await this.renderAvailableViewOptions();

        $(() => {
            $(this.selectElement).select2({ theme: 'bootstrap-5', width: "300px" });
        });
    }
    private async updateViews() {
        let views = await this.viewManagerHandlers.getViews(this);
        views = views.sort((a, b) => {
            if (a.Id === 'default') return -1;
            if (b.Id === 'default') return 1;
            return a.DisplayName.localeCompare(b.DisplayName);
        });
        this.views = views;
    }
}
interface ViewManagerHandlers {
    addButtonHandler: (e: MouseEvent, viewManager: ViewManager) => void;
    editButtonHandler: (e: MouseEvent, viewManager: ViewManager) => void;
    saveButtonHandler: (e: MouseEvent, viewManager: ViewManager) => void;
    shareButtonHandler: (e: MouseEvent, viewManager: ViewManager) => void;
    deleteButtonHandler: (e: MouseEvent, viewManager: ViewManager) => void;
    select2ChangeHandler: (viewManager: ViewManager) => Promise<void>;
    getViews: (viewManager: ViewManager) => Promise<ViewManagerView[]>;
}
